import moment from 'moment';
import { getUserData } from '../../../helpers/localStorage';

export const EMPTY_VALUE = '—';

// '2134504.01' -> '2134504': the release number without its sequence suffix
export const getReleaseBaseNumber = releaseNumber => {
  if (!releaseNumber) return EMPTY_VALUE;
  const value = String(releaseNumber);
  const dot = value.lastIndexOf('.');
  return dot === -1 ? value : value.slice(0, dot);
};

const STATUS_BADGES = {
  active_funded: { label: 'Active funded', type: 'success' },
  active_unfunded: { label: 'Active unfunded', type: 'warning' },
  nfa: { label: 'NFA', type: 'default-light' }
};

const TRIAGE_SOURCE_LABELS = {
  spell_line_item: 'a SPELL line item'
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

// A status the front does not know yet still reads as words, not as a raw token
const humanize = value => {
  const words = String(value)
    .replace(/_/g, ' ')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const getStatusBadge = status =>
  STATUS_BADGES[status] || { label: status ? humanize(status) : EMPTY_VALUE, type: 'default' };

// Saved dates may be 'YYYY-MM-DD' or a full timestamp; every date here is read by its date part
export const toDateOnly = value => String(value ?? '').split('T')[0];

export const formatDate = value => {
  if (!value) return EMPTY_VALUE;

  const date = moment(toDateOnly(value), 'YYYY-MM-DD', true);
  return date.isValid() ? date.format('MM/DD/YYYY') : EMPTY_VALUE;
};

export const formatIncident = value => value || 'pending';

export const formatFundingSource = value => value || 'None';

export const formatAssignee = assignee => assignee?.name || 'Unassigned';

// Open releases have no NFA date; the column is left blank for them rather than showing a dash
export const formatNfaDate = value => (value ? formatDate(value) : '');

export const formatCurrency = value => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const number = Number(value);
  return Number.isFinite(number) ? usd.format(number) : EMPTY_VALUE;
};

// Release Case Details reached from this tab; the Remediation module will use /release_details/:id
export const getReleaseDetailsPath = (facilityId, releaseId) =>
  `/facility_profile/${facilityId}/release_details/${releaseId}`;

export const getCostSheetPath = (facilityId, releaseId, costSheetId) =>
  `/facility_profile/${facilityId}/release_details/${releaseId}/cost_sheets/${costSheetId}`;

export const getTriageSourceLabel = source => TRIAGE_SOURCE_LABELS[source] || 'another record';

// Text after the release number in the triage banner; the work order is optional
export const getTriageMessage = ({ source, work_order_number: workOrderNumber }) => {
  const workOrder = workOrderNumber ? ` on work order ${workOrderNumber}` : '';
  return ` was promoted from ${getTriageSourceLabel(source)}${workOrder} and still needs triage.`;
};

// Env vars are strings, so compare with 'true': 'false' is truthy
export const isRemediationMockEnabled = () =>
  process.env.REACT_APP_IS_LOCAL === 'true' && process.env.REACT_APP_MOCK_REMEDIATION === 'true';

const COST_SHEET_STATE_BADGES = {
  draft: { label: 'Draft - pending approval', type: 'warning' },
  approved: { label: 'Approved - baseline', type: 'success' },
  superseded: { label: 'Superseded', type: 'default' }
};

// Same date-only rule as formatDate, in the long form used by Release Details
export const formatLongDate = value => {
  if (!value) return EMPTY_VALUE;

  const date = moment(toDateOnly(value), 'YYYY-MM-DD', true);
  return date.isValid() ? date.format('MMM D, YYYY') : EMPTY_VALUE;
};

export const formatKeyInfoNfaDate = value => {
  const formatted = formatLongDate(value);
  return formatted === EMPTY_VALUE ? '----' : formatted;
};

export const formatYesNo = value => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return EMPTY_VALUE;
};

// Amounts are USD with up to 2 decimals; a value that rounds to $0.00 has no sign to show
export const formatSignedCurrency = value => {
  const number = Number(value);
  if (value === null || value === undefined || value === '' || !Math.round(number * 100)) {
    return '-----';
  }
  return `${number > 0 ? '+' : '-'}${usd.format(Math.abs(number))}`;
};

export const getCostSheetStateBadge = state =>
  COST_SHEET_STATE_BADGES[state] || {
    label: state ? humanize(state) : EMPTY_VALUE,
    type: 'default'
  };

export const formatApproval = (approvedAt, approvedBy) => {
  if (!approvedAt) return '---';
  return `${formatDate(approvedAt)} ${approvedBy?.name || ''}`.trim();
};

export const formatPreparedBy = preparedBy => {
  if (!preparedBy) return EMPTY_VALUE;
  const { company, name } = preparedBy;
  return [company, name].filter(Boolean).join(' - ') || EMPTY_VALUE;
};

export const formatCommentDate = value => {
  if (!value) return EMPTY_VALUE;
  const date = moment(value, moment.ISO_8601);
  return date.isValid() ? date.format('MMM D, YYYY hh:mm A') : EMPTY_VALUE;
};

export const getInitials = author =>
  `${(author?.first_name || '').charAt(0)}${(author?.last_name || '').charAt(0)}`.toUpperCase();

export const COORDINATOR_ROLE = 'Environmental Specialist';

// Never throws: the storage can be missing (tests, private mode).
const getRoles = user => {
  try {
    const roles = (user === undefined ? getUserData() : user)?.roles;
    return Array.isArray(roles) ? roles : [];
  } catch (e) {
    return [];
  }
};

// A user with the coordinator role gets the coordinator flow, even with other roles too
export const getRemediationView = user =>
  getRoles(user).includes(COORDINATOR_ROLE) ? 'coordinator' : 'consultant';

// Role shown in the cost sheet banner: the one that picked the flow, else the first role
export const getEditorRoleLabel = user => {
  const roles = getRoles(user);
  return roles.includes(COORDINATOR_ROLE) ? COORDINATOR_ROLE : roles[0] || '';
};
